/* 
  REFINED TIMELINE ENGINE 
  - Fixed timezone-related variance bugs
  - Uses absolute date-string comparison for deadline health
*/
export const generateId = (prefix = '') => {
  const uuid = crypto.randomUUID();
  return prefix ? `${prefix}_${uuid.split('-')[0]}` : uuid;
};

export const toLocalISO = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDuration = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

export const STATUS_MAP = {
  'todo': { label: 'To Do', color: '#EF4444' },
  'in_progress': { label: 'In Progress', color: '#10B981' },
  'completed': { label: 'Completed', color: '#FFFFFF' }
};

export const normalizeStatus = (status) => {
  if (!status) return 'todo';
  const clean = status.toLowerCase().trim().replace(/[-_ ]/g, '_');
  if (clean === 'not_started' || clean === 'todo') return 'todo';
  if (clean === 'in_progress' || clean === 'progress') return 'in_progress';
  if (clean === 'completed' || clean === 'done' || clean === 'complete') return 'completed';
  return 'todo';
};

export const getStatusColor = (status) => {
  const key = normalizeStatus(status);
  return STATUS_MAP[key]?.color || '#9CA3AF';
};

/**
 * Calculates days between two YYYY-MM-DD strings accurately
 */
const getDayDiff = (startStr, endStr) => {
  const start = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  const diffTime = end.getTime() - start.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

export const calculateSchedule = (project) => {
  const startDateStr = project.startDate || project.start_date || toLocalISO(new Date());
  const workflows = project.workflows || [];

  if (!workflows.length) {
    return {
      ...project,
      startDate: startDateStr,
      calculatedEndDate: startDateStr,
      scheduledWorkflows: [],
      totalMinutes: 0,
      totalHours: 0,
      isFullyComplete: false,
      deadlineVariance: project.deadline ? getDayDiff(startDateStr, project.deadline) : 0
    };
  }

  let currentPointer = new Date(startDateStr + 'T12:00:00');
  const scheduledWorkflows = [];
  let totalProjectMinutes = 0;

  workflows.forEach((wf, wfIdx) => {
    let wfMinutes = 0;
    const scheduledTasks = [];
    let taskPointer = new Date(currentPointer);

    (wf.tasks || []).forEach((task, tIdx) => {
      const durationMin = task.durationMinutes || task.duration_minutes || 0;
      const daysNeeded = Math.max(1, Math.ceil(durationMin / (8 * 60)));
      
      const taskStart = new Date(taskPointer);
      const taskEnd = new Date(taskPointer);
      taskEnd.setDate(taskEnd.getDate() + daysNeeded - 1);

      scheduledTasks.push({
        ...task,
        _wfIndex: wfIdx,
        _taskIndex: tIdx,
        startDate: toLocalISO(taskStart),
        endDate: toLocalISO(taskEnd),
        durationMinutes: durationMin,
        status: normalizeStatus(task.status)
      });

      wfMinutes += durationMin;
      taskPointer = new Date(taskEnd);
      taskPointer.setDate(taskPointer.getDate() + 1);
    });

    const wfStart = new Date(currentPointer);
    const wfEnd = scheduledTasks.length > 0 
      ? new Date(scheduledTasks[scheduledTasks.length - 1].endDate + 'T12:00:00') 
      : new Date(currentPointer);

    const allDone = scheduledTasks.length > 0 && scheduledTasks.every(t => t.status === 'completed');
    const anyStarted = scheduledTasks.some(t => t.status === 'in_progress' || t.status === 'completed');
    
    const workflowStatus = allDone ? 'completed' : (anyStarted ? 'in_progress' : 'todo');

    scheduledWorkflows.push({
      ...wf,
      _wfIndex: wfIdx,
      calculatedStatus: workflowStatus,
      startDate: toLocalISO(wfStart),
      endDate: toLocalISO(wfEnd),
      scheduledTasks,
      totalMinutes: wfMinutes,
      totalHours: Math.ceil(wfMinutes / 60)
    });

    totalProjectMinutes += wfMinutes;
    currentPointer = new Date(wfEnd);
    currentPointer.setDate(currentPointer.getDate() + 1);
  });

  const calculatedEndDateStr = scheduledWorkflows.length > 0 
    ? scheduledWorkflows[scheduledWorkflows.length - 1].endDate 
    : startDateStr;

  let deadlineVariance = 0;
  if (project.deadline) {
    // Variance = Deadline Date - Calculated Finish Date
    // Positive means finish is BEFORE deadline (On Track)
    // Negative means finish is AFTER deadline (Overdue)
    deadlineVariance = getDayDiff(calculatedEndDateStr, project.deadline);
  }

  return {
    ...project,
    startDate: startDateStr,
    calculatedEndDate: calculatedEndDateStr,
    scheduledWorkflows,
    totalMinutes: totalProjectMinutes,
    totalHours: Math.ceil(totalProjectMinutes / 60),
    isFullyComplete: scheduledWorkflows.length > 0 && scheduledWorkflows.every(wf => wf.calculatedStatus === 'completed'),
    deadlineVariance
  };
};

export const getPriorityColor = (priority) => {
  switch(priority) {
    case 'critical': return 'text-red-400 bg-red-400/10 border-red-400/20';
    case 'high': return 'text-accent bg-accent/10 border-accent/20';
    case 'medium': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    case 'low': return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    default: return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  }
};